// frontend/src/components/CreatePost/CreatePost.jsx
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL } from "../../config/api";
import Swal from "sweetalert2";
import PostForm from "./PostForm";
import PhotoUploader from "./PhotoUploader";
import TermsCheckbox from "./TermsCheckbox";
import SubmitButton from "./SubmitButton";
import PublicationCounter from "../PublicationCounter"; // 👈 NUEVO
import { AlertTriangle, AlertCircle } from "lucide-react"; // 👈 NUEVO

export default function CreatePost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    titulo: "",
    categoria: "",
    tipo: "Venda",
    condicao: "usado",
    preco: "",
    descricao: "",
    fotos: [],
  });

  const [termsAccepted, setTermsAccepted] = useState({
    ownership: false,
    truthfulness: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  
  // 🔥 NUEVO: Estado para el límite de publicaciones
  const [publicationStats, setPublicationStats] = useState(null);
  const [canPost, setCanPost] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // 🔥 NUEVO: Verificar límite al cargar
  useEffect(() => {
    const checkLimit = async () => {
      setStatsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get(`${API_BASE_URL}/user/publication-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPublicationStats(response.data);
        setCanPost(!response.data.is_exceeded);
      } catch (error) {
        console.error('Error checking publication limit:', error);
        // Si hay error, permitir publicar (fallback seguro)
        setCanPost(true);
      } finally {
        setStatsLoading(false);
      }
    };
    checkLimit();
  }, []);

  // Verificar si el usuario puede publicar
  useEffect(() => {
    if (user && user.status !== 'aprovado') {
      Swal.fire({
        icon: "info",
        title: "Conta em validação",
        text: "Aguarde a aprovação do seu cadastro para publicar anúncios.",
        confirmButtonColor: "#449779",
      }).then(() => navigate("/"));
    }
  }, [user, navigate]);

  // Cargar datos de texto guardados (sin fotos)
  useEffect(() => {
    const savedData = localStorage.getItem("createPostForm");
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setFormData(prev => ({
        ...prev,
        titulo: parsedData.titulo || "",
        categoria: parsedData.categoria || "",
        tipo: parsedData.tipo || "Venda",
        condicao: parsedData.condicao || "usado",
        preco: parsedData.preco || "",
        descricao: parsedData.descricao || "",
      }));
      setTermsAccepted({
        ownership: parsedData.accept_ownership || false,
        truthfulness: parsedData.accept_truthfulness || false,
      });
      setIsFormDirty(true);
    }
  }, []);

  // Guardar SOLO datos de texto en localStorage
  useEffect(() => {
    const dataToSave = {
      titulo: formData.titulo,
      categoria: formData.categoria,
      tipo: formData.tipo,
      condicao: formData.condicao,
      preco: formData.preco,
      descricao: formData.descricao,
      accept_ownership: termsAccepted.ownership,
      accept_truthfulness: termsAccepted.truthfulness,
    };
    localStorage.setItem("createPostForm", JSON.stringify(dataToSave));
  }, [formData.titulo, formData.categoria, formData.tipo, formData.condicao, formData.preco, formData.descricao, termsAccepted]);

  // Limpiar datos guardados
  const clearSavedData = () => {
    localStorage.removeItem("createPostForm");
    setIsFormDirty(false);
  };

  // Limpiar formato del precio
  const cleanPrice = (formattedPrice) => {
    if (!formattedPrice) return "";
    const rawValue = formattedPrice
      .replace(/R\$\s?/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    return rawValue;
  };

  // Validación del formulario
  const validate = () => {
    const newErrors = {};
    if (!formData.titulo.trim()) newErrors.titulo = "Título é obrigatório";
    if (!formData.categoria.trim()) newErrors.categoria = "Categoria é obrigatória";
    if (!formData.descricao.trim()) newErrors.descricao = "Descrição é obrigatória";
    
    if (formData.tipo === "Venda") {
      const cleanPreco = cleanPrice(formData.preco);
      if (!cleanPreco) {
        newErrors.preco = "Preço é obrigatório para venda";
      } else if (isNaN(parseFloat(cleanPreco)) || parseFloat(cleanPreco) <= 0) {
        newErrors.preco = "Preço deve ser maior que zero";
      }
    }
    
    if (formData.fotos.length === 0) newErrors.fotos = "Pelo menos 1 foto é obrigatória";

    if (!termsAccepted.ownership) {
      newErrors.terms = "Você precisa declarar ser proprietário do item";
    }
    if (!termsAccepted.truthfulness) {
      newErrors.terms = "Você precisa confirmar que as informações são verdadeiras";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔥 NUEVO: Submit con verificación de límite
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🔥 Verificar límite nuevamente antes de publicar
    if (!canPost) {
      Swal.fire({
        icon: "warning",
        title: "Limite excedido",
        text: "Sua unidade já atingiu o limite de 10 publicações neste mês. Aguarde o próximo mês.",
        confirmButtonColor: "#449779",
      });
      return;
    }

    if (!validate()) {
      // Mostrar el primer error
      const firstError = Object.values(errors)[0];
      if (firstError) {
        Swal.fire({
          icon: "warning",
          title: "Campos incompletos",
          text: firstError,
          confirmButtonColor: "#449779",
        });
      }
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Usuário não autenticado");

      const formPayload = new FormData();
      formPayload.append("titulo", formData.titulo);
      formPayload.append("categoria", formData.categoria);
      formPayload.append("tipo", formData.tipo);
      formPayload.append("condicao", formData.condicao);
      
      if (formData.tipo === "Venda") {
        const cleanPreco = cleanPrice(formData.preco);
        formPayload.append("preco", cleanPreco);
      }
      
      formPayload.append("descricao", formData.descricao);
      formPayload.append("accept_ownership", termsAccepted.ownership);
      formPayload.append("accept_truthfulness", termsAccepted.truthfulness);

      formData.fotos.forEach((file) => {
        formPayload.append("fotos[]", file);
      });

      const response = await axios.post(`${API_BASE_URL}/posts`, formPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // 🔥 Mostrar mensaje con información del límite
      const remaining = response.data.remaining_publications || 0;
      let message = "Seu anúncio foi enviado para análise e será publicado em breve.";
      if (remaining <= 2 && remaining > 0) {
        message += ` ⚠️ Restam apenas ${remaining} publicação${remaining > 1 ? 'ões' : ''} neste mês.`;
      } else if (remaining === 0) {
        message += " 🚫 Limite de 10 publicações atingido. Aguarde o próximo mês.";
      }

      Swal.fire({
        icon: "success",
        title: "Publicação criada!",
        text: message,
        confirmButtonColor: "#449779",
      });

      // Reset form
      setFormData({
        titulo: "",
        categoria: "",
        tipo: "Venda",
        condicao: "usado",
        preco: "",
        descricao: "",
        fotos: [],
      });
      setTermsAccepted({ ownership: false, truthfulness: false });
      clearSavedData();
      
      // Actualizar estadísticas
      const statsResponse = await axios.get(`${API_BASE_URL}/user/publication-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPublicationStats(statsResponse.data);
      setCanPost(!statsResponse.data.is_exceeded);
      
      navigate("/");
    } catch (error) {
      console.error("Erro ao criar publicação:", error.response || error);
      
      // 🔥 Manejar error de límite específico (429)
      if (error.response?.status === 429) {
        Swal.fire({
          icon: "warning",
          title: "Limite excedido",
          text: error.response?.data?.message || "Sua unidade já atingiu o limite de 10 publicações neste mês.",
          confirmButtonColor: "#449779",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text: error.response?.data?.message || "Erro ao criar publicação",
          confirmButtonColor: "#449779",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTermsChange = (accepted) => {
    setTermsAccepted(accepted);
    if (errors.terms) {
      setErrors(prev => ({ ...prev, terms: false }));
    }
  };

  // 🔥 Mostrar loading mientras se verifica el límite
  if (statsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-28 px-4">
        <div className="max-w-lg mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#449879] mx-auto mb-4"></div>
            <p className="text-gray-500">Verificando limite de publicações...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-28 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Criar Anúncio</h1>
          <p className="text-sm text-gray-500 mt-1">
            Compartilhe com sua comunidade
          </p>
        </div>

        {/* 🔥 NUEVO: CONTADOR DE PUBLICACIONES */}
        <div className="mb-6">
          <PublicationCounter />
        </div>

        {/* 🔥 ALERTA DE LÍMITE - Cerca del límite */}
        {publicationStats && publicationStats.is_near_limit && !publicationStats.is_exceeded && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              ⚠️ Restam apenas {publicationStats.remaining} publicações neste mês.
            </p>
          </div>
        )}

        {/* 🔥 ALERTA DE LÍMITE - Excedido */}
        {publicationStats && publicationStats.is_exceeded && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              🚫 Limite de 10 publicações atingido. Aguarde o próximo mês.
            </p>
          </div>
        )}

        {/* Aviso de datos salvos */}
        {isFormDirty && formData.titulo && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-600 text-center">
            💾 Seus dados foram salvos automaticamente
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <PostForm
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
          
          <PhotoUploader
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />
          
          <TermsCheckbox
            onAcceptChange={handleTermsChange}
            errors={errors}
          />
          
          <SubmitButton loading={loading} />
        </form>

        {/* Botón para limpiar rascunho */}
        {isFormDirty && (
          <button
            type="button"
            onClick={() => {
              Swal.fire({
                title: "Limpar rascunho?",
                text: "Todas as informações não salvas serão perdidas.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#ef4444",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "Sim, limpar",
                cancelButtonText: "Cancelar",
              }).then((result) => {
                if (result.isConfirmed) {
                  setFormData({
                    titulo: "",
                    categoria: "",
                    tipo: "Venda",
                    condicao: "usado",
                    preco: "",
                    descricao: "",
                    fotos: [],
                  });
                  setTermsAccepted({ ownership: false, truthfulness: false });
                  clearSavedData();
                  Swal.fire("Rascunho limpo!", "", "success");
                }
              });
            }}
            className="mt-4 text-xs text-red-500 hover:text-red-700 text-center w-full"
          >
            🗑️ Limpar rascunho
          </button>
        )}
      </div>
    </div>
  );
}